import { CheckIcon } from "lucide-react";
import moment from "moment";
import * as React from "react";

import { Button, DropdownMenu, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

import { getStatusColumnId, VIDEO_PROJECT_STATUS_COLUMNS, VideoProjectStatusColumnId } from "./columns";
import { VideoProjectStatusIcon, statusDisplayLabel } from "./status";
import { getValidStatusColumnIds } from "./transitions";

export type { VideoProjectStatusColumnId, VideoProjectStatusColumnDef } from "./columns";
export { VIDEO_PROJECT_STATUS_COLUMNS, getStatusColumnId, getRepresentativeStatusForColumn } from "./columns";
export { VideoProjectStatusIcon, statusDisplayLabel } from "./status";
export { getValidStatusColumnIds, getVideoProjectEventForStatusChange } from "./transitions";
export type { VideoProjectStatusChangeRequest } from "./transitions";

interface VideoProjectStatusPickerProps {
  status: VideoProject.Status;
  statusDate?: string | null;
  requiresQa?: boolean | null;
  currentUserId?: number;
  compact?: boolean;
  labeled?: boolean;
  interactive?: boolean;
  disabled?: boolean;
  onSelect?: (columnId: VideoProjectStatusColumnId) => void;
}

function statusTooltip(label: string, statusDate?: string | null) {
  if (!statusDate) return label;
  const date = moment(statusDate);
  if (!date.isValid()) return label;
  return `${label} · ${date.format("MMM D, YYYY [at] h:mm a")}`;
}

function stopPointer(event: { stopPropagation(): void }) {
  event.stopPropagation();
}

export function VideoProjectStatusPicker({
  status,
  statusDate,
  requiresQa,
  currentUserId,
  compact = false,
  labeled = false,
  interactive = true,
  disabled = false,
  onSelect,
}: VideoProjectStatusPickerProps) {
  const currentColumnId = getStatusColumnId(status);
  const label = statusDisplayLabel(status);
  const tooltipLabel = statusTooltip(label, statusDate);

  const validColumnIds = React.useMemo(
    () =>
      new Set(
        getValidStatusColumnIds(status, {
          requiresQa,
          currentUserId,
        })
      ),
    [currentUserId, requiresQa, status]
  );

  const triggerClassName = "size-6 rounded-sm text-muted-foreground hover:bg-muted";
  const canChange = interactive && !disabled;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!canChange) setOpen(false);
  }, [canChange]);

  const menuContent = (
    <DropdownMenu.Content align={compact ? "end" : "start"} className="min-w-52">
      <DropdownMenu.Group>
        <DropdownMenu.Label>Status</DropdownMenu.Label>
        {VIDEO_PROJECT_STATUS_COLUMNS.map(column => {
          const selected = column.id === currentColumnId;
          const enabled = selected || validColumnIds.has(column.id);
          const itemLabel = selected ? label : column.label;

          return (
            <DropdownMenu.Item
              key={column.id}
              disabled={!enabled}
              className="gap-2 py-1.5"
              onClick={() => {
                if (!selected) {
                  setOpen(false);
                  onSelect?.(column.id);
                }
              }}
            >
              {selected ? <VideoProjectStatusIcon status={status} /> : <VideoProjectStatusIcon columnId={column.id} />}
              <span className="truncate">{itemLabel}</span>
              {selected ? <CheckIcon className="ml-auto size-3.5" /> : null}
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Group>
    </DropdownMenu.Content>
  );

  return (
    <div
      data-kanban-interactive=""
      className={cn("pointer-events-auto shrink-0", !labeled && "mt-0.5")}
      onPointerDown={stopPointer}
      onMouseDown={stopPointer}
      onClick={stopPointer}
    >
      <Tooltip>
        <DropdownMenu open={open} onOpenChange={nextOpen => canChange && setOpen(nextOpen)}>
          <DropdownMenu.Trigger
            disabled={!canChange}
            aria-label={labeled ? `Status: ${label}` : `Status: ${tooltipLabel}`}
            render={({ className, ...triggerProps }) => {
              const sharedClassName = typeof className === "string" ? className : undefined;

              return (
                <Tooltip.Trigger
                  render={
                    labeled ? (
                      <button
                        {...triggerProps}
                        type="button"
                        disabled={!canChange}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-md outline-none",
                          canChange ? "cursor-pointer" : "cursor-default",
                          sharedClassName
                        )}
                      >
                        <VideoProjectStatusIcon status={status} />
                        <span className="text-sm font-medium text-slate-700">{label}</span>
                      </button>
                    ) : (
                      <Button
                        {...triggerProps}
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        disabled={!canChange}
                        className={cn(triggerClassName, sharedClassName)}
                      >
                        <VideoProjectStatusIcon status={status} />
                      </Button>
                    )
                  }
                />
              );
            }}
          />
          {menuContent}
        </DropdownMenu>
        {labeled ? null : <Tooltip.Content>{tooltipLabel}</Tooltip.Content>}
      </Tooltip>
    </div>
  );
}
